import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import DataTable, { TableColumn } from "../components/ui/DataTable";
import AddNewButton from "../components/ui/AddNewButton";
import Toggle from "../components/ui/Toggle";
import Modal from "../components/layout/Modal";
import DeleteModal from "../components/layout/DeleteModal";
import RewardForm, { RewardFormValues } from "../components/rewards/RewardForm";
import EmptyState from "../components/ui/EmptyState";
import TablePagination from "../components/ui/TablePagination";
import { useRewards, useUpdateReward, useDeleteReward, Reward } from "../services/rewards";
import { useLoyaltyTiers } from "../services/loyalty";
import ROUTES from "../utils/ROUTES";
import { toast } from "../hooks/use-toast";
import StatusPill from "../components/ui/StatusPill";
import { Pencil, Trash2, Coins } from "lucide-react";
import axios from "axios";

const getErrorMessage = (err: unknown): string =>
  axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : "Something went wrong. Please try again.";

const Rewards = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [deletingReward, setDeletingReward] = useState<Reward | null>(null);

  const { data: rewardsData, isLoading, isError } = useRewards(currentPage);
  const rewards = rewardsData?.rewards;
  const pagination = rewardsData?.pagination;
  const { data: loyaltyTiers } = useLoyaltyTiers();

  const tierMap = useMemo(() => {
    const map = new Map<number, { name: string; color: string; min_points: number; max_points: number }>();
    loyaltyTiers?.forEach((t) => map.set(Number(t.id), { name: t.name, color: t.color, min_points: t.min_points, max_points: t.max_points }));
    return map;
  }, [loyaltyTiers]);

  const groupedRewards = useMemo(() => {
    if (!rewards) return [];
    const groups = new Map<number, Reward[]>();
    rewards.forEach((r) => {
      if (!groups.has(r.loyalty_tier_id)) groups.set(r.loyalty_tier_id, []);
      groups.get(r.loyalty_tier_id)!.push(r);
    });
    return Array.from(groups.entries())
      .map(([tierId, tierRewards]) => {
        const tier = tierMap.get(tierId);
        return {
          tierId,
          tierName: tier?.name ?? `Tier ${tierId}`,
          tierColor: tier?.color,
          tierMinPoints: tier?.min_points,
          tierMaxPoints: tier?.max_points,
          rewards: [...tierRewards].sort((a, b) => a.points - b.points),
        };
      })
      .sort((a, b) => (a.tierMinPoints ?? 0) - (b.tierMinPoints ?? 0));
  }, [rewards, tierMap]);
  const { mutate: updateReward, isPending: isUpdating } = useUpdateReward();
  const { mutate: deleteReward } = useDeleteReward();

  const handleConfirmDelete = () => {
    if (!deletingReward) return;
    deleteReward(deletingReward.id, {
      onSuccess: () => setDeletingReward(null),
    });
  };

  const handleSave = (values: RewardFormValues) => {
    if (!editingReward) return;
    updateReward(
      { id: editingReward.id, ...values },
      {
        onSuccess: () => { toast({ variant: "success", title: "Reward updated", description: "The reward has been successfully updated." }); setEditingReward(null); },
        onError: (err) => { toast({ variant: "error", title: "Failed to update reward", description: getErrorMessage(err) }); },
      }
    );
  };

  const handleToggle = (reward: Reward, is_enabled: boolean) => {
    const { name, description, points, expiry_date, loyalty_tier_id, image_url } = reward;
    updateReward({ id: reward.id, name, description, points, expiry_date, loyalty_tier_id, image_url, is_enabled });
  };

  const columns: TableColumn<Reward>[] = [
    {
      key: "image",
      header: "Image",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="w-12 h-12 rounded-[2px] border border-[#c2c7c9] overflow-hidden shrink-0">
          <img src={row.image_url} alt={row.name} className="w-full h-full object-cover" />
        </div>
      ),
    },
    {
      key: "description",
      header: "Reward Description",
      width: "50%",
      cellClassName: "px-6 py-[22px]",
      render: (row) => (
        <div className="flex flex-col gap-[2px]">
          <span className="text-[16px] font-bold leading-none text-content-heading">
            {row.name}
          </span>
          <span className="text-[14px] font-normal text-content-label">
            {row.description.length > 250 ? row.description.slice(0, 250) + "…" : row.description}
          </span>
        </div>
      ),
    },
    {
      key: "points",
      header: "Points",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-normal text-content-body">
            {row.points.toLocaleString()} Points
          </span>
          <Coins className="w-[13px] h-[13px] shrink-0" />
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cellClassName: "px-6 py-[30px]",
      render: (row) => <StatusPill status={row.is_enabled ? "Active" : "InActive"} />,
    },
    {
      key: "actions",
      header: "Actions",
      headerAlign: "right",
      width: "180px",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <div className="relative group">
            <button
              onClick={() => setEditingReward(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Edit reward"
            >
              <Pencil className="w-[15px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Edit reward</span>
          </div>
          <div className="relative group">
            <button
              onClick={() => setDeletingReward(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Delete reward"
            >
              <Trash2 className="w-[13px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Delete reward</span>
          </div>
          <div className="pl-2 flex items-center">
            <Toggle
              enabled={row.is_enabled}
              onChange={(val) => handleToggle(row, val)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 lg:pl-11 lg:pr-8 pt-6 lg:pt-[60px] pb-16">
        {/* Mobile hamburger */}
        <div className="flex items-center mb-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="#012926" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page title row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-[28px] sm:text-[34px] uppercase tracking-[0.68px] leading-none text-brand-dark"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              Rewards
            </h1>
            <p className="mt-3 text-[14px] sm:text-[16px] font-medium text-content-label">
              Real-time overview of program health and customer engagement.
            </p>
          </div>

          <AddNewButton onClick={() => navigate(ROUTES.ADD_REWARD)} />
        </div>

        <div className="mt-8 lg:mt-[36px]">
          {isLoading && <p className="text-[14px] text-content-label">Loading…</p>}
          {isError && <p className="text-[14px] text-content-error">Failed to load rewards.</p>}
          {!isLoading && !isError && (
            !rewards?.length
              ? <EmptyState message="No rewards found." />
              : (
                  <>
                    {groupedRewards.map(({ tierId, tierName, tierColor, tierMinPoints, tierMaxPoints, rewards: tierRewards }) => (
                      <div key={tierId} className="mb-10">
                        <div className="flex items-center gap-2 mb-4">
                          {tierColor && (
                            <span
                              className="inline-block w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: tierColor }}
                            />
                          )}
                          <h2 className="text-[18px] font-semibold tracking-wide text-content-heading uppercase">
                            {tierName}
                          </h2>
                          {tierMinPoints !== undefined && tierMaxPoints !== undefined && (
                            <span className="text-[13px] font-normal text-content-label normal-case">
                              ({tierMinPoints.toLocaleString()} – {tierMaxPoints.toLocaleString()} pts)
                            </span>
                          )}
                        </div>
                        <DataTable columns={columns} rows={tierRewards} rowKey={(r) => r.id} />
                      </div>
                    ))}
                    <TablePagination currentPage={currentPage} pagination={pagination} rowCount={rewards!.length} onPageChange={setCurrentPage} />
                  </>
                )
          )}
        </div>
      </main>

      <Modal
        isOpen={editingReward !== null}
        onClose={() => setEditingReward(null)}
        title={`Edit "${editingReward?.name ?? ""}"`}
      >
        <RewardForm
          initialValues={{
            name: editingReward?.name,
            description: editingReward?.description,
            points: editingReward?.points,
            expiry_date: editingReward?.expiry_date?.split("T")[0],
            loyalty_tier_id: editingReward?.loyalty_tier_id,
            image_url: editingReward?.image_url,
          }}
          isPending={isUpdating}
          onSave={handleSave}
        />
      </Modal>

      <DeleteModal
        isOpen={deletingReward !== null}
        onClose={() => setDeletingReward(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this reward?"
        message={
          <>
            This action will permanently remove the{" "}
            <span
              className="font-semibold text-content-body"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              &ldquo;{deletingReward?.name}&rdquo;
            </span>{" "}
            reward from your system. This action cannot be undone.
          </>
        }
        confirmLabel="Delete Reward"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Rewards;
